"use client";

import { useRef, type MutableRefObject } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";
import { Image } from "@/helpers/Image";
import type { PageTradingCompetitionSchema } from "@/sanity/pages/PageTradingCompetitionTemplate";
import CtaHover from "./CtaHover";
import TradingCompetitionCountdown, { unitLabelsFor } from "./TradingCompetitionCountdown";
import { useCtaHref } from "./ctaHref";
import SelectionHighlight, { type SelectionGeometry } from "./SelectionHighlight";
import { at, StableText, useCountUp, useReveal } from "./motion";

/**
 * Entrance (one clock from mount, ~2.6s, everything overlapping):
 *   the big serif "$200,000" surfaces out of a heavy blur → the trophy unveils bottom-up and
 *   settles from a zoom → pill, title, copy, checks and buttons rise out of a blur → the
 *   selection paints across "$200,000" while it counts up from $0 → each check draws its tick
 *   → the countdown card rises last and its progress line paints in.
 * Scroll: the background word lags the most, the copy a little, and the trophy lifts, so the
 *   layers read at three depths.
 * Resting state === the QA-ed layout.
 */
const AT = {
  word: 0.1,
  trophy: 0.35,
  chip: 0.15,
  title: 0.3,
  highlight: 1.0,
  copy: 0.5,
  checks: 0.62,
  ticks: 0.95,
  buttons: 0.82,
  countdown: 1.15,
} as const;

/** Fills a text with a top-down gradient; the em padding keeps the italic overhang painted. */
const clipText = (to: string) => ({
  backgroundImage: `linear-gradient(180deg, #d1e6e0 0%, rgba(235,242,240,0) ${to})`,
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
});

/** "$200,000" selection: Figma 179×44 at 40px (mobile), 269×58 at 56px (desktop), measured against the glyphs. */
const HERO_SELECTION = {
  mobile: {
    font: 40,
    box: [-6.13, -5.66, 179, 44],
    word: 166.33,
    handles: [
      [-0.75, 0, 1.5, 40],
      [-2, 40, 4, 4],
      [177, 0, 4, 4],
      [178.25, 4, 1.5, 40],
    ],
  },
  desktop: {
    font: 56,
    box: [-16.89, -6.23, 269, 58],
    word: 232.86,
    handles: [
      [-2, 0, 2, 52],
      [-4, 52, 6, 6],
      [265, 0, 6, 6],
      [267, 6, 2, 52],
    ],
  },
} satisfies Record<string, SelectionGeometry>;

function Check({ label, index }: { label: string; index: number }) {
  return (
    <li
      className="tc-rv-rise flex w-full items-center gap-[1rem] border border-solid border-black/10 bg-black/5 p-[1.25rem] lg:w-auto lg:border-0 lg:bg-transparent lg:p-0"
      style={at(AT.checks + index * 0.08)}>
      {/* the Figma check (24px black square, white tick), inline so the tick can draw itself */}
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        className="h-[3rem] w-[3rem] shrink-0">
        <rect
          width="24"
          height="24"
          rx="2"
          fill="black"
        />
        <path
          className="tc-rv-draw"
          d="M6.5 12.275L10.075 15.85L17.5 8.15"
          stroke="white"
          pathLength={1}
          strokeDasharray={1}
          style={at(AT.ticks + index * 0.14)}
        />
      </svg>
      <p className="mb-0 font-geist text-[2rem] leading-[normal] font-medium whitespace-nowrap text-black">{label}</p>
    </li>
  );
}

export default function TradingCompetitionHero({ data }: { data: PageTradingCompetitionSchema }) {
  const language = data.settings?.language;
  // The German title is too wide for the fixed Figma lines, so it wraps freely there.
  const isGerman = stegaClean(language) === "de";
  const primaryHref = useCtaHref(data.heroPrimaryCta, language);
  const secondaryHref = useCtaHref(data.heroSecondaryCta, language);

  const [revealRef, reveal] = useReveal<HTMLElement>({ now: true });
  const amount = useCountUp(data.heroTitleHighlight, reveal === "live", { delay: AT.highlight, duration: 1.7 });

  const reduceMotion = useReducedMotion();
  const scrollRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({ target: scrollRef, offset: ["start start", "end start"] });
  const wordY = useTransform(scrollYProgress, [0, 1], ["0rem", "14rem"]);
  const copyY = useTransform(scrollYProgress, [0, 1], ["0rem", "6rem"]);
  const trophyY = useTransform(scrollYProgress, [0, 1], ["0rem", "-4rem"]);
  const depth = (y: typeof wordY) => (reduceMotion ? undefined : { y });

  return (
    <section
      ref={(el) => {
        (revealRef as MutableRefObject<HTMLElement | null>).current = el;
        scrollRef.current = el;
      }}
      data-reveal={reveal}
      className="relative isolate lg:h-[127.125rem]">
      {/* Background: white with the texture at 50%. It starts under the nav, as in the Figma frame;
          on mobile a white fade closes it at the bottom (on desktop that fade sits under the white). */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-[-7.625rem] bottom-0 -z-10 bg-white lg:top-[-7.375rem]">
        {data.heroBackground && (
          <Image
            src={data.heroBackground}
            alt=""
            fill
            sizes="100vw"
            quality={90}
            loading="eager"
            className="object-cover opacity-50"
            style={{ width: "100%", height: "100%" }}
          />
        )}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0)_56.67%,#fff_100%)] lg:hidden" />
      </div>

      {/* ── Copy ───────────────────────────────────────────────────────── */}
      <div
        className={`relative z-10 px-[2rem] pt-[5rem] lg:absolute lg:top-[6.25rem] lg:left-1/2  lg:-translate-x-1/2 lg:p-0 ${isGerman ? "lg:w-[95rem]" : "lg:w-[85.5rem]"}`}>
        <motion.div
          className="flex flex-col items-center"
          style={depth(copyY)}>
          {data.heroChip && (
            <div
              style={at(AT.chip)}
              className="tc-rv-rise mb-[1.25rem] flex items-center gap-[1.075rem] overflow-hidden rounded-[0.215rem] border-[0.1075rem] border-solid border-black/10 bg-black/5 px-[1.505rem] py-[0.86rem] lg:hidden">
              <i
                aria-hidden="true"
                className="h-[0.86rem] w-[0.86rem] shrink-0 rounded-full bg-[#125a24]"
              />
              <p className="mb-0 font-geist text-[1.505rem] leading-[normal] font-normal whitespace-nowrap text-black/[0.72]">
                {data.heroChip}
              </p>
            </div>
          )}

          <h1
            style={at(AT.title, 1)}
            className={`tc-rv-rise mb-0 translate-x-[0.75rem] text-center font-geist text-[5rem] leading-[1.04] font-medium tracking-[-0.225rem] ${isGerman ? "lg:whitespace-pre" : "whitespace-pre"} text-black lg:translate-x-0 lg:text-[7rem] lg:tracking-[-0.315rem]`}>
            {data.heroTitleLine1}
            {"\n"}
            {data.heroTitleBefore}
            {/* Figma's own spacing around the highlight: three spaces before on desktop, two on mobile */}
            <span className="lg:hidden">{"  "}</span>
            <span className="hidden lg:inline">{"   "}</span>
            {/* inline with line-height 0, so the serif face does not grow the line box */}
            <span className="relative font-blacker leading-[0] font-normal text-[#0b5b45] italic">
              <SelectionHighlight
                {...HERO_SELECTION}
                fill="rgba(11,91,69,0.1)"
                handle="#0b5b45"
                delay={AT.highlight}
              />
              {/* counts up from $0 over an invisible copy, so the line never reflows */}
              <span className="relative font-blacker">
                <StableText
                  text={data.heroTitleHighlight}
                  shown={amount}
                  align="right"
                />
              </span>
            </span>
            <span className="font-blacker leading-[0] font-normal italic lg:hidden"> </span>
            <span className="hidden font-blacker leading-[0] font-normal italic lg:inline">{"  "}</span>
            <span className="hidden lg:inline"> </span>
            {data.heroTitleAfter}
          </h1>

          {data.heroDescription && (
            <p
              style={at(AT.copy)}
              className="tc-rv-rise mt-[3rem] mb-0 w-[40.5rem] text-center font-geist text-[2rem] leading-[1.5] font-normal text-black/[0.72] lg:mt-[2.75rem] lg:w-[85.5rem] lg:text-[2.25rem]">
              {data.heroDescription}
            </p>
          )}

          {!!data.heroChecks?.length && (
            <ul className="mt-[2.5rem] mb-0 flex w-full list-none flex-col gap-[1.125rem] p-0 lg:mt-[3rem] lg:w-auto lg:flex-row lg:gap-[6rem]">
              {data.heroChecks.map((label, i) => (
                <Check
                  key={i}
                  label={label}
                  index={i}
                />
              ))}
            </ul>
          )}

          <div
            style={at(AT.buttons)}
            className="tc-rv-rise mt-[2.5rem] flex w-full flex-col gap-[1.25rem] lg:mt-[4rem] lg:w-auto lg:flex-row lg:gap-[2rem]">
            {data.heroPrimaryCta?.text && (
              <a
                href={primaryHref}
                className="group relative flex h-[6rem] items-center justify-center overflow-hidden rounded-[0.25rem] px-[3rem] lg:h-[5.875rem] lg:px-[3.5rem]">
                <CtaHover
                  label={data.heroPrimaryCta.text}
                  fills={["bg-black", "bg-[#307f6a]"]}
                  labelClassName="font-geist text-[1.875rem] leading-[normal] font-medium whitespace-nowrap text-white lg:w-[18.75rem] lg:text-center lg:text-[1.75rem] lg:leading-[1.347] lg:font-semibold lg:tracking-[-0.035rem]"
                />
              </a>
            )}
            {data.heroSecondaryCta?.text && (
              <a
                href={secondaryHref}
                className="group relative flex h-[6rem] items-center justify-center overflow-hidden rounded-[0.25rem] border border-solid border-black/10 bg-black/5 px-[3rem] font-geist text-[1.875rem] leading-[normal] font-medium whitespace-nowrap text-black backdrop-blur-[3.6063rem] lg:h-auto lg:w-[16.125rem] lg:p-[1rem] lg:text-[1.75rem] lg:backdrop-blur-[0.25rem]">
                <CtaHover label={data.heroSecondaryCta.text} />
              </a>
            )}
          </div>
        </motion.div>
      </div>

      {/* ── Stage: background word, trophy, countdown ──────────────────── */}
      <div className="relative h-[59.875rem] w-full lg:absolute lg:inset-0 lg:mt-[8rem] lg:h-auto">
        {data.heroBackgroundWord && (
          <motion.p
            aria-hidden="true"
            className="tc-rv-rise absolute top-[18.0125rem] left-[calc(50%-25.875rem)] -mx-[0.2em] mb-0 px-[0.2em] font-blacker text-[11.4687rem] leading-[0.9444] font-normal whitespace-nowrap italic lg:hidden"
            style={{
              ...clipText("100%"),
              ...at(AT.word, 2.2),
              ["--rise" as string]: "4rem",
              ["--blur" as string]: "24px",
              ...depth(wordY),
            }}>
            {data.heroBackgroundWord}
          </motion.p>
        )}
        {data.heroBackgroundWord && (
          <motion.p
            aria-hidden="true"
            className="tc-rv-rise absolute top-[58.875rem] left-[calc(50%-102.25rem)] -mx-[0.2em] mb-0 hidden px-[0.2em] font-blacker text-[45rem] leading-[0.9444] font-normal whitespace-nowrap italic lg:block"
            style={{
              ...clipText("82.647%"),
              ...at(AT.word, 2.2),
              ["--rise" as string]: "4rem",
              ["--blur" as string]: "24px",
              ...depth(wordY),
            }}>
            {data.heroBackgroundWord}
          </motion.p>
        )}

        {data.heroTrophy && (
          <div
            style={at(AT.trophy, 1.4)}
            className="tc-rv-unveil absolute top-[3rem] left-[calc(50%-0.05rem)] h-[39.92rem] w-[26.4rem] -translate-x-1/2 overflow-hidden lg:top-[46.375rem] lg:left-[calc(50%-0.0338rem)] lg:h-[60.625rem] lg:w-[47.7476rem]">
            <motion.div
              className="tc-rv-zoom absolute inset-0 origin-bottom"
              style={{ ...at(AT.trophy, 1.9), ["--zoom" as string]: "1.18", ...depth(trophyY) }}>
              <Image
                src={data.heroTrophy}
                alt=""
                sizes="(max-width: 992px) 60vw, 25vw"
                quality={100}
                loading="eager"
                className="absolute top-[2.11%] left-0 max-w-none lg:top-[0.04%]"
                style={{ width: "100%", height: "auto" }}
              />
            </motion.div>
          </div>
        )}

        <TradingCompetitionCountdown
          label={data.countdownLabel}
          target={data.countdownTarget}
          unitLabels={unitLabelsFor(language)}
          className="top-[35.875rem] left-[calc(50%-23.125rem)] lg:top-[96.25rem] lg:left-[calc(50%-0.875rem)] lg:-translate-x-1/2"
          revealDelay={AT.countdown}
        />
      </div>
    </section>
  );
}
